import { createRoot } from 'react-dom/client'
import { Core, WebViewerInstance } from '@pdftron/webviewer'
import { JobManifest } from '~/graphql/hooks/useJobManifestQuery'
import DualScrollHeaderButton from '~/components/WebviewerComponent/DualScrollButton'
import { Switch } from '~/components/ui/Switch'
import Label from '~/components/ui/Label'
import SmartMatchButton from '~/components/WebviewerComponent/SmartMatchButton'

// Constants
const DEBOUNCE_DELAY = 300
const MAX_LABEL_LENGTH_DROPDOWN = 60
const MAX_LABEL_LENGTH_HEADER = 40
const TRUNCATE_LENGTH = 40
const BREAKPOINT_WIDTH = 1366
const DROPDOWN_OFFSET_LEFT = 28

// Style IDs to prevent duplicate style injection
const STYLE_IDS = {
  PAGE_SEARCH_DROPDOWN: 'mbue-page-search-dropdown-styles',
  PAGE_SEARCH_LIST_ITEM: 'mbue-page-search-list-item-styles',
  LAYER_TOGGLE: 'mbue-layer-toggle-styles'
} as const

/**
 * Creates a debounced version of a function
 * Properly maintains closure state for timeout management
 */
const createDebounce = <T extends (...args: Parameters<T>) => void>(
  fn: T,
  delay: number
): ((...args: Parameters<T>) => void) => {
  let timeoutId: NodeJS.Timeout | null = null

  return (...args: Parameters<T>) => {
    if (timeoutId !== null) {
      clearTimeout(timeoutId)
    }
    timeoutId = setTimeout(() => {
      fn(...args)
      timeoutId = null
    }, delay)
  }
}

/**
 * Checks if a page has valid sheet information
 */
const hasValidSheetInfo = (jobManifest: JobManifest, pageIndex: number): boolean => {
  if (!jobManifest.matches || jobManifest.matches.length === 0) {
    return false
  }

  const match = jobManifest.matches[pageIndex]
  return !!(match?.revisedSheetNumber && match?.revisedSheetTitle)
}

/**
 * Gets the sheet label for a page
 */
const getSheetLabel = (jobManifest: JobManifest, pageIndex: number): string | null => {
  if (!hasValidSheetInfo(jobManifest, pageIndex)) {
    return null
  }

  const match = jobManifest.matches[pageIndex]
  return `${match.revisedSheetNumber} - ${match.revisedSheetTitle}`
}

/**
 * Truncates a label if it exceeds the maximum length
 */
const truncateLabel = (label: string, maxLength: number): string => {
  if (label.length <= maxLength) {
    return label
  }
  return `${label.substring(0, TRUNCATE_LENGTH)}...`
}

/**
 * Generates a page label with optional sheet information
 */
export const getPageLabel = (currentPage: number, jobManifest: JobManifest): string => {
  const pageIndex = currentPage - 1
  let label = `Page ${currentPage}`

  const sheetLabel = getSheetLabel(jobManifest, pageIndex)
  if (sheetLabel) {
    label += ` | ${sheetLabel}`
    label = truncateLabel(label, MAX_LABEL_LENGTH_DROPDOWN)
  }

  return label
}

/**
 * Generates a header page label (without "Page X" prefix for sheet info)
 */
const getHeaderPageLabel = (currentPage: number, jobManifest: JobManifest): string => {
  const pageIndex = currentPage - 1
  const sheetLabel = getSheetLabel(jobManifest, pageIndex)

  if (sheetLabel) {
    return truncateLabel(sheetLabel, MAX_LABEL_LENGTH_HEADER)
  }

  return `Page ${currentPage}`
}

/**
 * Injects styles into document head if not already present
 */
const injectStyles = (iframeDoc: Document, styleId: string, styles: string): void => {
  if (iframeDoc.getElementById(styleId)) {
    return
  }

  const style = document.createElement('style')
  style.id = styleId
  style.textContent = styles
  iframeDoc.head.appendChild(style)
}

/**
 * Creates a page list item element for the search dropdown
 */
const createPageListItem = (
  iframeDoc: Document,
  label: string,
  onClick: () => void
): HTMLLIElement => {
  const listItemStyles = `
    .mbue-page-search-list-item {
      box-sizing: border-box;
      width: 100%;
      height: 30px;
      border-radius: 4px;
      padding-inline: 8px;
      display: flex;
      align-items: center;
    }

    .mbue-page-search-list-item:hover {
      background-color: #4b4b4b;
      cursor: pointer;
    }

    .mbue-page-search-list-item span {
      font-size: 12px;
    }
  `

  injectStyles(iframeDoc, STYLE_IDS.PAGE_SEARCH_LIST_ITEM, listItemStyles)

  const listItem = document.createElement('li')
  listItem.classList.add('mbue-page-search-list-item')
  listItem.setAttribute('role', 'option')
  listItem.setAttribute('aria-label', label)

  listItem.innerHTML = `
    <div style="font-size: 14px; width: 100%; overflow: hidden; text-overflow: ellipsis;" tabindex="0" role="button">
      <span style="text-wrap: nowrap;">${label}</span>
    </div>
  `

  listItem.addEventListener('click', onClick)
  listItem.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault()
      onClick()
    }
  })

  return listItem
}

/**
 * Manages the page search dropdown functionality
 */
const createPageSearchDropdown = (
  webviewerInstance: WebViewerInstance,
  iframeDoc: Document,
  jobManifest: JobManifest,
  totalPages: number,
  updatePageLabel: (pageNumber: number) => void
): {
  container: HTMLDivElement
  input: HTMLInputElement
  cleanup: () => void
} => {
  const setCurrentPage = (pageNumber: number, smoothScroll: boolean): void => {
    webviewerInstance.Core.documentViewer.setCurrentPage(pageNumber, smoothScroll)
  }

  const dropdownStyles = `
    .mbue-page-search-dropdown {
      background-color: #1e1e1e;
      border-radius: 4px;
      box-shadow: 0 0 10px rgba(0, 0, 0, 0.5);
      box-sizing: border-box;
      display: none;
      flex-direction: column;
      align-items: center;
      gap: 8px;
      max-height: 270px;
      padding: 8px 0;
      position: fixed;
      top: 40px;
      width: 350px;
      z-index: 1000;
    }

    .mbue-page-search-dropdown--visible {
      display: flex;
    }

    .mbue-page-search-input {
      background-color: rgb(51, 51, 51);
      border-radius: 4px;
      border: 1px solid #485056;
      box-sizing: border-box;
      color: rgb(248, 249, 250);
      font-size: 12px;
      height: 30px;
      outline: none;
      padding: 4px 8px;
      width: calc(100% - 16px);
    }

    .mbue-page-search-list {
      box-sizing: border-box;
      flex-grow: 1;
      list-style-type: none;
      margin: 0;
      overflow-y: auto;
      padding: 0;
      width: 100%;
    }
  `

  injectStyles(iframeDoc, STYLE_IDS.PAGE_SEARCH_DROPDOWN, dropdownStyles)

  // Create page labels array
  const pageLabels = Array.from({ length: totalPages }, (_, index) => {
    const pageNumber = index + 1
    return createPageListItem(iframeDoc, getPageLabel(pageNumber, jobManifest), () => {
      setCurrentPage(pageNumber, false)
      updatePageLabel(pageNumber)
    })
  })

  const dropdownContainer = document.createElement('div')
  dropdownContainer.classList.add('mbue-page-search-dropdown')
  dropdownContainer.setAttribute('role', 'listbox')
  dropdownContainer.setAttribute('aria-label', 'Page search results')

  const inputSearch = document.createElement('input')
  inputSearch.placeholder = 'Type the page number or sheet title...'
  inputSearch.classList.add('mbue-page-search-input')
  inputSearch.type = 'text'
  inputSearch.setAttribute('role', 'combobox')
  inputSearch.setAttribute('aria-expanded', 'false')
  inputSearch.setAttribute('aria-controls', 'mbue-page-search-list')
  inputSearch.setAttribute('aria-autocomplete', 'list')

  const pagesList = document.createElement('ul')
  pagesList.id = 'mbue-page-search-list'
  pagesList.classList.add('mbue-page-search-list')
  pagesList.setAttribute('role', 'listbox')
  pagesList.append(...pageLabels)

  const handleSearch = (value: string): void => {
    if (!value.trim()) {
      pagesList.innerHTML = ''
      pagesList.append(...pageLabels)
      return
    }

    const searchTerm = value.toLowerCase()
    const filteredPageLabels = pageLabels.filter((pageLabel) => {
      return pageLabel.innerText.toLowerCase().includes(searchTerm)
    })

    pagesList.innerHTML = ''
    if (filteredPageLabels.length > 0) {
      pagesList.append(...filteredPageLabels)
      inputSearch.setAttribute('aria-expanded', 'true')
    } else {
      inputSearch.setAttribute('aria-expanded', 'false')
    }
  }

  const debouncedSearch = createDebounce(handleSearch, DEBOUNCE_DELAY)

  const handleKeyup = (event: KeyboardEvent): void => {
    const { value } = event.target as HTMLInputElement
    debouncedSearch(value)
  }

  inputSearch.addEventListener('keyup', handleKeyup)

  const handleKeyboardNavigation = (e: KeyboardEvent): void => {
    if (!dropdownContainer.classList.contains('mbue-page-search-dropdown--visible')) {
      return
    }

    const focusableElements = dropdownContainer.querySelectorAll<HTMLElement>(
      'input, [tabindex="0"]'
    )
    const firstElement = focusableElements[0]
    const lastElement = focusableElements[focusableElements.length - 1]

    if (!firstElement || !lastElement) {
      return
    }

    // Handle arrow key navigation
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const currentElement = iframeDoc.activeElement
      const listItems = Array.from(focusableElements).slice(1)

      if (currentElement === inputSearch) {
        if (e.key === 'ArrowDown' && listItems.length > 0) {
          listItems[0].focus()
        }
        return
      }

      const currentIndex = listItems.indexOf(currentElement as HTMLElement)
      if (currentIndex === -1) {
        return
      }

      let nextIndex: number
      if (e.key === 'ArrowDown') {
        nextIndex = currentIndex === listItems.length - 1 ? 0 : currentIndex + 1
      } else {
        nextIndex = currentIndex === 0 ? listItems.length - 1 : currentIndex - 1
      }

      listItems[nextIndex]?.focus()
      return
    }

    // Handle Tab key navigation (circular)
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        if (iframeDoc.activeElement === firstElement) {
          e.preventDefault()
          lastElement.focus()
        }
      } else {
        if (iframeDoc.activeElement === lastElement) {
          e.preventDefault()
          firstElement.focus()
        }
      }
      return
    }

    // Close dropdown on Escape
    if (e.key === 'Escape') {
      dropdownContainer.classList.remove('mbue-page-search-dropdown--visible')
      inputSearch.setAttribute('aria-expanded', 'false')
      inputSearch.blur()
    }
  }

  dropdownContainer.addEventListener('keydown', handleKeyboardNavigation)

  dropdownContainer.appendChild(inputSearch)
  dropdownContainer.appendChild(pagesList)
  iframeDoc.body.appendChild(dropdownContainer)

  const handleClickOutside = (event: MouseEvent): void => {
    const pageNumberElement = iframeDoc.querySelector<HTMLDivElement>(
      '[data-element="pageNumber"]'
    )

    if (
      dropdownContainer &&
      pageNumberElement &&
      !dropdownContainer.contains(event.target as Node) &&
      !pageNumberElement.contains(event.target as Node)
    ) {
      dropdownContainer.classList.remove('mbue-page-search-dropdown--visible')
      inputSearch.setAttribute('aria-expanded', 'false')
    }
  }

  iframeDoc.addEventListener('click', handleClickOutside)

  // Cleanup function
  const cleanup = (): void => {
    inputSearch.removeEventListener('keyup', handleKeyup)
    dropdownContainer.removeEventListener('keydown', handleKeyboardNavigation)
    iframeDoc.removeEventListener('click', handleClickOutside)
    dropdownContainer.remove()
  }

  return {
    container: dropdownContainer,
    input: inputSearch,
    cleanup
  }
}

export const headerCustomizations = (
  webviewerInstance: WebViewerInstance,
  jobManifest: JobManifest,
  jobType: string,
  totalPages: number,
  flags?: Record<string, boolean> | null | undefined
): void => {
  const iframeDoc = webviewerInstance.UI.iframeWindow.document

  /**
   * Updates the page label in the header based on the current page number
   */
  const updatePageLabel = (currentPage: number): void => {
    const pageNumberElement = iframeDoc.querySelector<HTMLDivElement>(
      '[data-element="pageNumber"]'
    )

    if (!pageNumberElement) {
      return
    }

    const label = getHeaderPageLabel(currentPage, jobManifest)
    pageNumberElement.textContent = label
  }

  // Store dropdown references for cleanup
  let dropdownRef: ReturnType<typeof createPageSearchDropdown> | null = null

  webviewerInstance.UI.setHeaderItems((header) => {
    const headerItems = header
      .getItems()
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .filter((item: Record<string, any>) => item.element !== 'viewControlsOverlay')

    const nextpageButton = {
      type: 'actionButton',
      img: 'icon-chevron-right',
      onClick: () => {
        const pageCount = webviewerInstance.Core.documentViewer.getPageCount()
        const currentPage = webviewerInstance.Core.documentViewer.getCurrentPage()
        if (currentPage >= pageCount) {
          return
        }

        const nextPage = currentPage + 1
        webviewerInstance.Core.documentViewer.setCurrentPage(nextPage, false)
        updatePageLabel(nextPage)
      },
      dataElement: 'mbuePageNavigationButtonPrev',
      hidden: ['mobile']
    }

    const prevpageButton = {
      type: 'actionButton',
      img: 'icon-chevron-left',
      onClick: () => {
        const currentPage = webviewerInstance.Core.documentViewer.getCurrentPage()
        if (currentPage <= 1) {
          return
        }

        const prevPage = currentPage - 1
        webviewerInstance.Core.documentViewer.setCurrentPage(prevPage, false)
        updatePageLabel(prevPage)
      },
      dataElement: 'mbuePageNavigationButtonNext',
      hidden: ['mobile']
    }

    const pageNumber = {
      type: 'customElement',
      render: () => {
        const params = new URLSearchParams(window.location.search)
        const pageNumberParam = params.get('pageNumber')
          ? parseInt(params.get('pageNumber')!, 10)
          : 0

        const initialPage =
          pageNumberParam === 0
            ? webviewerInstance.Core.documentViewer.getCurrentPage()
            : pageNumberParam

        const label = getHeaderPageLabel(initialPage, jobManifest)
        return <div data-element="pageNumber">{label}</div>
      }
    }

    const dualScrollButton = {
      type: 'customElement',
      title: 'Dual Scrolling (D)',
      render: () => (
        <DualScrollHeaderButton
          jobType={jobType}
          isSmartMatch={/\/smart-match\/?$/.test(window.location.pathname)}
        />
      )
    }

    const smartMatchButton = {
      type: 'customElement',
      title: 'Smart Match (M)',
      render: () => (
        <SmartMatchButton isSmartMatchEnabled={flags?.isSmartMatchEnabled ?? false} />
      )
    }

    const snippingToolButton = {
      type: 'toolGroupButton',
      toolGroup: 'snippingTools',
      dataElement: 'snippingToolGroupButton',
      title: 'annotation.snipping'
    }

    const polygonToolButton = {
      type: 'toolGroupButton',
      toolGroup: 'polygonTools',
      dataElement: 'polygonToolButton',
      title: 'annotation.polygon'
    }

    const layerToggles = {
      type: 'customElement',
      dataElement: 'layersToggleMenuButton',
      render: () => {
        const { documentViewer, annotationManager } = webviewerInstance.Core
        const doc = documentViewer.getDocument()
        const wrapperDiv = document.createElement('div')
        wrapperDiv.classList.add('mbue-layer-toggle-container')
        let layersState: Core.Document.LayerContext[] = []

        // Since we're within webviewer, we need to import tailwind. We can use the defaults here.
        const script = document.createElement('script')
        script.src = 'https://cdn.tailwindcss.com'
        wrapperDiv.appendChild(script)

        const layerToggleStyles = `
          .mbue-layer-toggle-container {
            display: flex;
            align-items: center;
            gap: 16px;
            margin-inline-end: 16px;
          }

          .mbue-layer-toggle {
            display: flex;
            align-items: center;
            gap: 6px;
            white-space: nowrap;
          }

          .primary {
            color: var(--view-header-button-active);
          }
        `

        injectStyles(iframeDoc, STYLE_IDS.LAYER_TOGGLE, layerToggleStyles)

        // Order and rename the layers as needed
        const toggles = new Map([
          ['mbue Smart Diff', 'Smart Overlay'],
          ['mbue Smart Overlay', 'Smart Overlay'],
          ['Original', 'Original'],
          ['Revised', 'Revised']
        ])

        // Reorder the layers based on the toggles map
        // If a layer is not in the map, it will be placed at the end
        function getOrderedLayers(): Promise<Core.Document.LayerContext[]> {
          return new Promise<Core.Document.LayerContext[]>((resolve) => {
            doc
              ?.getLayersArray()
              .then((layers) => {
                const unmatchedLayers = layers.filter((layer) => !toggles.has(layer.name))
                const matchedLayers: Core.Document.LayerContext[] = [...toggles.keys()]
                  .map((key) => layers.find((layer) => layer.name === key))
                  .filter((layer): layer is Core.Document.LayerContext => !!layer)

                resolve([...matchedLayers, ...unmatchedLayers])
              })
              .catch(() => {
                resolve([])
              })
          })
        }

        function getLayerId(layerName: string): string {
          return `layer-${layerName.replace(/[^a-z0-9]/gi, '')}`
        }

        // If the layer is the Smart Overlay, toggle the annotations.
        // We only need to call this if the user uses the switches. The built in
        // Apryse checkboxes handles hiding annotations with the layer.
        function toggleAnnotations(layer: Core.Document.LayerContext): void {
          const annotations = annotationManager.getAnnotationsList()
          const mbueAnnotations = annotations.filter(
            (annot) => (annot.Author ?? '').toLowerCase() === 'mbue'
          )

          if (layer.name.toLowerCase().includes('mbue smart')) {
            if (layer.visible) {
              annotationManager.showAnnotations(mbueAnnotations)
            } else {
              annotationManager.hideAnnotations(mbueAnnotations)
            }
          }
        }

        // While we're rendering react components for this, we are still outside of the react ecosystem
        // We need to update the component state using vanilla js
        function updateToggleElements(): void {
          void getOrderedLayers().then((layers) => {
            layersState = layers

            layersState.forEach((layer) => {
              const state = layer.visible ? 'checked' : 'unchecked'
              const layerId = `#${getLayerId(layer.name)}`
              const buttonEl = wrapperDiv.querySelector<HTMLElement>(layerId)

              if (buttonEl) {
                const dotEl = buttonEl.firstElementChild
                ;[buttonEl, dotEl].forEach((el) => {
                  if (el) {
                    el.setAttribute('data-state', state)
                    if (el.getAttribute('aria-checked')) {
                      el.setAttribute('aria-checked', state)
                    }
                  }
                })
              }
            })
          })
        }

        const handlePageComplete = (): void => {
          updateToggleElements()
        }

        documentViewer.addEventListener('pageComplete', handlePageComplete)

        // On header render, get the layers and render the toggles
        void getOrderedLayers().then((layers) => {
          layersState = layers

          layersState.forEach((layer) => {
            const layerId = getLayerId(layer.name)
            const layerDiv = document.createElement('div')
            layerDiv.classList.add('mbue-layer-toggle')

            wrapperDiv.appendChild(layerDiv)
            const root = createRoot(layerDiv)

            // to make this cleaner, we'll render our shadcn react
            // components and append the output to our parent div
            root.render(
              <>
                <Label htmlFor={layerId} className="text-sm font-semibold cursor-pointer">
                  {toggles.get(layer.name) ?? layer.name}
                </Label>
                <Switch
                  id={layerId}
                  checked={layer.visible}
                  onCheckedChange={() => {
                    layersState = layersState.map((layerState) => {
                      if (layerState.name === layer.name) {
                        layerState.visible = !layerState.visible
                        toggleAnnotations(layerState)
                      }
                      return layerState
                    })
                    doc.setLayersArray(layersState)
                    documentViewer.refreshAll()
                    documentViewer.updateView()
                    updateToggleElements()
                  }}
                  className="primary h-3.5 w-6 bg-gray-900 data-[state=checked]:bg-current"
                  toggleClassName="bg-white opacity-70 w-2.5 h-2.5 data-[state=checked]:opacity-100 data-[state=checked]:translate-x-2.5"
                />
              </>
            )
          })
        })

        return jobType === 'drawing' ? wrapperDiv : null
      }
    }

    const isSmartMatchEnabled =
      jobType === 'drawing' &&
      (window.location.pathname.endsWith('/view') ||
        window.location.pathname.startsWith('/admin'))
    const isPolygonToolEnabled =
      jobType === 'drawing' && window.location.pathname.endsWith('/smart-match')

    switch (jobType) {
      case 'drawing':
      case 'text':
        header.update([
          headerItems[0], // viewer menu
          headerItems[2], // left panel button
          headerItems[1], // divider
          headerItems[4], // zoom controls
          headerItems[14], // Marquee Zoom Tool
          headerItems[5], // Annotations Edit - Arrow Tool
          headerItems[6], // Pan - Hand tool
          headerItems[7], // layers menu
          isPolygonToolEnabled ? polygonToolButton : {},
          dualScrollButton,
          headerItems[3], // divider
          prevpageButton,
          pageNumber,
          nextpageButton,
          headerItems[8], // spacer
          isSmartMatchEnabled ? smartMatchButton : {},
          flags?.isLayerToggleEnabled ? layerToggles : {},
          headerItems[11], // search panel
          headerItems[12] // notes panel - comments
        ])
        break
      case 'bounding-box':
        header.update([
          headerItems[0], // viewer menu
          headerItems[2], // left panel button
          headerItems[1], // divider
          headerItems[4], // zoom controls
          headerItems[14], // Marquee Zoom Tool
          headerItems[5], // Annotations Edit - Arrow Tool
          headerItems[6], // Pan - Hand tool
          snippingToolButton, // Snipping Tool
          dualScrollButton,
          headerItems[3], // divider
          prevpageButton,
          pageNumber,
          nextpageButton
        ])
        break
      case 'product-requirements':
        header.update([
          headerItems[0], // viewer menu
          headerItems[2], // left panel button
          headerItems[1], // divider
          headerItems[4], // zoom controls
          headerItems[14], // Marquee Zoom Tool
          headerItems[5], // Annotations Edit - Arrow Tool
          headerItems[6] // Pan - Hand tool
        ])
        break
      case 'product-schedule':
        header.update([
          headerItems[0], // viewer menu
          headerItems[2], // left panel button
          headerItems[1], // divider
          headerItems[4], // zoom controls
          headerItems[14], // Marquee Zoom Tool
          headerItems[5], // Annotations Edit - Arrow Tool
          headerItems[6], // Pan - Hand tool
          dualScrollButton,
          headerItems[3], // divider
          headerItems[8], // spacer
          headerItems[11], // search panel
          headerItems[12] // notes panel - comments
        ])
        break
    }
  })

  // Set the color CSS property of the polygon tool button SVG to white, if it exists
  const polygonToolSvg =
    webviewerInstance.UI.iframeWindow.document.querySelector<SVGElement>(
      '[data-element="polygonToolButton"] svg'
    )
  if (polygonToolSvg) {
    polygonToolSvg.style.color = 'white'
  }

  // This is a workaround to place the newest on the right side and keep the comments arrows
  // pointing to the comments in the newests
  if (jobType === 'text') {
    const multiViewerContainer = iframeDoc.querySelector<HTMLDivElement>('.MultiViewer')
    if (multiViewerContainer) {
      multiViewerContainer.style.flexDirection = 'row-reverse'
    }
  }

  if (!['drawing', 'text', 'bounding-box'].includes(jobType)) {
    return
  }

  const pageNumberElement = iframeDoc.querySelector<HTMLDivElement>(
    '[data-element="pageNumber"]'
  )

  const prevNextButtons = iframeDoc.querySelectorAll<HTMLButtonElement>(
    '[data-element^="mbuePageNavigationButton"]'
  )

  prevNextButtons.forEach((navButton) => {
    navButton.style.backgroundColor = '#1e1e1e'
    navButton.style.width = '24px'
    navButton.style.height = '24px'
    navButton.style.borderRadius = '4px'
    navButton.style.display = 'flex'
    navButton.style.justifyContent = 'center'
    navButton.style.alignItems = 'center'
    const icon = navButton.querySelector<HTMLDivElement>('.Icon')
    if (icon) {
      icon.style.width = '18px'
      icon.style.height = '18px'
    }
  })

  if (pageNumberElement) {
    pageNumberElement.style.backgroundColor = '#1e1e1e'
    pageNumberElement.style.width = '294px'
    pageNumberElement.style.paddingInline = '12px'
    pageNumberElement.style.height = '24px'
    pageNumberElement.style.borderRadius = '4px'
    pageNumberElement.style.display = 'flex'
    pageNumberElement.style.justifyContent = 'center'
    pageNumberElement.style.alignItems = 'center'
    pageNumberElement.style.fontSize = '12px'
    pageNumberElement.style.overflow = 'hidden'
    pageNumberElement.style.textOverflow = 'ellipsis'
    pageNumberElement.style.textWrap = 'nowrap'
    pageNumberElement.style.whiteSpace = 'nowrap'
    pageNumberElement.style.cursor = 'pointer'

    const handlePageNumberClick = (): void => {
      if (!dropdownRef) {
        dropdownRef = createPageSearchDropdown(
          webviewerInstance,
          iframeDoc,
          jobManifest,
          totalPages,
          updatePageLabel
        )
      }

      const { container, input } = dropdownRef
      const isVisible = container.classList.toggle('mbue-page-search-dropdown--visible')

      if (isVisible) {
        const rect = pageNumberElement.getBoundingClientRect()
        container.style.left = `${rect.left - DROPDOWN_OFFSET_LEFT}px`
        input.setAttribute('aria-expanded', 'true')
        input.focus()
        input.value = ''
        input.dispatchEvent(new KeyboardEvent('keyup'))
      } else {
        input.setAttribute('aria-expanded', 'false')
      }
    }

    pageNumberElement.addEventListener('click', handlePageNumberClick)
  }

  // Move all the page navigation controls into a wrapper to be centered all together
  const pageNavigationControlsWrapper = document.createElement('div')
  pageNavigationControlsWrapper.classList.add('page-navigation-controls-wrapper')
  pageNavigationControlsWrapper.style.display = 'flex'
  pageNavigationControlsWrapper.style.gap = '4px'

  const controls = [prevNextButtons[0], pageNumberElement, prevNextButtons[1]]
  const firstControl = controls.find(
    (control) => control !== null && control !== undefined
  )

  if (firstControl?.parentNode) {
    firstControl.parentNode.insertBefore(pageNavigationControlsWrapper, firstControl)
  }

  controls.forEach((control) => {
    if (control) {
      pageNavigationControlsWrapper.appendChild(control)
    }
  })

  pageNavigationControlsWrapper.style.position = 'absolute'
  pageNavigationControlsWrapper.style.left = '50%'
  pageNavigationControlsWrapper.style.transform = 'translateX(-50%)'

  const handleResize = (): void => {
    if (dropdownRef) {
      dropdownRef.container.classList.remove('mbue-page-search-dropdown--visible')
      dropdownRef.input.setAttribute('aria-expanded', 'false')
    }

    if (window.innerWidth > BREAKPOINT_WIDTH) {
      pageNavigationControlsWrapper.style.position = 'absolute'
      pageNavigationControlsWrapper.style.left = '50%'
      pageNavigationControlsWrapper.style.transform = 'translateX(-50%)'
    } else {
      pageNavigationControlsWrapper.style.position = 'static'
      pageNavigationControlsWrapper.style.left = '0'
      pageNavigationControlsWrapper.style.transform = 'none'
    }
  }

  window.addEventListener('resize', handleResize)
}
